import { useSelector, useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import {
  getPreviews,
  getPreviewsLoading,
  getSelectedFoldersReadyStatus,
  getFolderMetadataPolling,
  getLocalFolderSelections,
} from "redux/CreateDataset/selectors";
import {
  calculatePlaceHolderCount,
  useWindowResize,
} from "helpers/utility/utilities";
import { MetadataType, PreviewItem, SetStateFunction } from "types";
import { formatNumberWithCommas } from "helpers/utility/formatters";
import { DATASET_CREATION_PAGE } from "helpers/constants/pages";
import {
  fetchDatasetPreviews,
  stopFolderMetadataPolling,
} from "redux/CreateDataset/actions";

import EnrichmentCard from "views/pages/DataPage/CardsView/DataList/EnrichmentCard";
import Loader from "../../Loader";

import styles from "./style.module.scss";

const PreviewCount = ({ count }: { count: number }) => {
  const countText = formatNumberWithCommas(count);

  return (
    <div className={styles.previewCount}>
      {countText} Image{count !== 1 ? "s" : ""}
    </div>
  );
};
interface PreviewContentProps {
  batchId?: string;
}
export default function PreviewContent({ batchId }: PreviewContentProps) {
  const dispatch = useDispatch();
  const previews = useSelector(getPreviews);
  const isLoading = useSelector(getPreviewsLoading);
  const selectedFoldersStatus = useSelector(getSelectedFoldersReadyStatus);
  const isPolling = useSelector(getFolderMetadataPolling);
  const selectedFolderIds = useSelector(getLocalFolderSelections);
  const [placeholderCount, setPlaceholderCount] = useState(0);
  const [previewsFetched, setPreviewsFetched] = useState(false);

  const previewContainerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const { height, width } = useWindowResize();
  const { newDatasetId } = useParams();

  // Watch for when selected folders become ready and fetch previews
  useEffect(() => {
    if (!newDatasetId || !selectedFoldersStatus.allReady || previewsFetched) {
      return;
    }

    const fetchPreviews = async () => {
      try {
        const success = await dispatch(
          fetchDatasetPreviews(newDatasetId, batchId) as any,
        );
        if (success) {
          setPreviewsFetched(true);
          // Stop folder polling once previews are loaded
          dispatch(stopFolderMetadataPolling() as any);
        }
      } catch (error) {
        console.error("Failed to fetch previews:", error);
      }
    };

    fetchPreviews();
  }, [
    dispatch,
    newDatasetId,
    selectedFoldersStatus.allReady,
    previewsFetched,
    batchId,
  ]);

  // Reset previews fetched status when selected folders change
  useEffect(() => {
    setPreviewsFetched(false);
  }, [selectedFolderIds]);

  // Empty functions for EnrichmentCard props
  const handleSelect = () => {};
  const handleSetVertex = () => {};
  const onGlobalMDTypeChange = () => {};
  const setIndexOfPointerOut: SetStateFunction<number> = () => {};

  useEffect(() => {
    const extraWidth = 60;

    let tempCount = calculatePlaceHolderCount(
      previewContainerRef.current,
      cardRef.current,
      previews.length,
      extraWidth,
    );
    if (placeholderCount !== tempCount) {
      setPlaceholderCount(tempCount);
    }
  }, [height, width, placeholderCount, previews.length]);

  /* 
  Show loading if:
      1. Previews are being fetched (isLoading)
      2. We're still polling for folders to become ready and haven't fetched previews yet 
  */
  const showLoading =
    isLoading ||
    (isPolling && !previewsFetched && !selectedFoldersStatus.allReady);

  return (
    <div className={styles.previewSection}>
      <div className={styles.previewTitle}>
        Previews
        {previews.length > 0 && <PreviewCount count={previews.length} />}
      </div>
      {showLoading ? (
        <Loader text="Loading Previews..." />
      ) : (
        <div className={styles.previewsContainer} ref={previewContainerRef}>
          {previews.map((preview: PreviewItem, index) => {
            // Transform PreviewItem to the format expected by EnrichmentCard
            const singleData = {
              ...preview,
              type: "IMAGE",
              media_id: preview.dataset_id,
              media_uri: preview.thumbnail,
              media_thumb_uri: preview.thumbnail,
              image_uri: preview.s3_url,
              original_uri: preview.s3_url,
              caption: null,
              labels: preview.image_annotations,
              user_tags: null,
              video_uri: null,
              bounding_box: null,
              frame_timestamp: null,
              relevance_score: null,
              relevance_score_type: null,
            };

            const globalMDType = preview.object_annotations?.length
              ? MetadataType.OBJECT_LABELS
              : MetadataType.IMAGE_LABELS;

            return (
              <EnrichmentCard
                key={index}
                index={index}
                clusterID=""
                isVertex={false}
                userTagsData={[]}
                isDataPage={false}
                modelType="IMAGE"
                isClusterPage={true}
                singleData={singleData}
                indexOfPointerOut={-10}
                showSelectOption={false}
                cardElementRef={cardRef}
                datasetId={newDatasetId!}
                globalMDType={globalMDType}
                showMetadataSummary={false}
                page={DATASET_CREATION_PAGE}
                containerRef={previewContainerRef}
                onSelect={handleSelect}
                onSetVertex={handleSetVertex}
                onIndexOfPointerOut={setIndexOfPointerOut}
                onGlobalMDTypeChange={onGlobalMDTypeChange}
              />
            );
          })}
          {previews.length < 1 &&
            [...Array(placeholderCount)].map((_, index) => (
              <div key={index} className={styles.cardPlaceHolder}></div>
            ))}
        </div>
      )}
    </div>
  );
}
